import type { ProjectBudgetStatus } from '../../lib/api'
import { budgetClasses, formatHours } from '../../lib/budget-view'
import { cn } from '../../lib/utils'

export function BudgetPeriodLabel({ status }: { status: ProjectBudgetStatus }) {
  if (!status.period) return null
  const date = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString('de-DE')
  return (
    <span>
      {date(status.period.fromDate)} – {date(status.period.toDate)}
    </span>
  )
}

export function BudgetSummary({
  status,
  compact = false,
}: {
  status: ProjectBudgetStatus
  compact?: boolean
}) {
  const pct = status.contingentHours ? ((status.usedHours ?? 0) / status.contingentHours) * 100 : 0
  const colors = budgetClasses(pct)
  return (
    <div className={cn('min-w-0', compact ? 'space-y-1' : 'space-y-2')}>
      <dl className={cn(compact ? 'flex flex-col gap-1' : 'grid grid-cols-3 gap-2')}>
        {(
          [
            ['Kontingent', status.contingentHours],
            ['Verbraucht', status.usedHours],
            ['Rest', status.remainingHours],
          ] as const
        ).map(([label, hours]) => (
          <div
            key={label}
            className={cn(
              'min-w-0 rounded-lg',
              compact
                ? 'flex flex-wrap items-baseline justify-between gap-x-2'
                : 'border border-border-subtle px-2 py-3',
              label === 'Rest' && !compact && 'border-accent/30 bg-accent/5',
            )}
          >
            <dt className="text-[11px] text-text-tertiary">{label}</dt>
            <dd
              className={cn(
                'font-medium tabular-nums break-words',
                compact ? 'text-sm' : 'mt-1 text-base lg:text-xl',
                label === 'Rest' && colors.value,
              )}
            >
              {formatHours(hours)}
            </dd>
          </div>
        ))}
      </dl>
      <div className="flex justify-between gap-2 text-xs text-text-tertiary">
        <span>Verbraucht</span>
        <span className={colors.text}>
          {pct.toLocaleString('de-DE', { maximumFractionDigits: 0 })} %
        </span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-bg-overlay"
        role="progressbar"
        aria-label="Kontingent verbraucht"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, Math.max(0, pct))}
        aria-valuetext={`${formatHours(status.usedHours)} von ${formatHours(status.contingentHours)}`}
      >
        <div
          className={cn('h-full transition-all', colors.bar)}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  )
}

export function BudgetRates({
  status,
  rows = false,
  dayLabel = 'Heute Ø',
}: {
  status: ProjectBudgetStatus
  rows?: boolean
  dayLabel?: string
}) {
  return (
    <dl className={cn(rows ? 'flex flex-col gap-1 justify-between' : 'grid grid-cols-3 gap-2')}>
      {(
        [
          [dayLabel, status.perDay, status.ownTodayHours],
          ['Woche', status.weekHours, status.ownWeekHours],
          ['Monat', status.monthHours, status.ownMonthHours],
        ] as const
      ).map(([label, rate, own]) => (
        <div
          key={label}
          className={cn(
            'min-w-0',
            rows
              ? 'grid grid-cols-[minmax(0,1fr)_auto] gap-x-1 items-baseline'
              : 'rounded-lg bg-bg-overlay/40 px-2 py-2',
          )}
        >
          <dt className="text-[11px] leading-4 text-text-secondary">{label}</dt>
          <dd className={cn('text-sm font-medium tabular-nums', rows && 'leading-4')}>
            {formatHours(rate)}
          </dd>
          <dd
            className={cn(
              'text-[10px] text-text-tertiary tabular-nums',
              rows && 'col-span-2 leading-3',
            )}
          >
            gebucht {formatHours(own)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function BudgetHint() {
  return (
    <p className="text-[11px] text-text-tertiary">
      Deine Raten für die verbleibende Zeit · Feiertage, Urlaub und Arbeitszeitmodell
      berücksichtigt.
    </p>
  )
}
